import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Card, CardBody, Row, Col, Button, Collapse
} from "reactstrap";
import {
  InstantSearch,
  useHits
} from "react-instantsearch";
import { createElasticsearchAdapter } from "../../utils/elasticsearchAdapter";
import {
  CustomSearchBox,
  CustomStats,
  CustomSortBy,
  CustomRefinementList,
  CustomClearRefinements,
  CustomPagination
} from "../applicant/CustomSearchComponents";
import PublicationsHits from "./Hits";

const STATE_LABELS = {
  active: "Activo",
  inactive: "Inactivo",
  draft: "Borrador"
};

const filterConfigs = [
  {
    id: "aasm_state",
    label: "Estado",
    field: "aasm_state.raw",
    placeholder: "Buscar estado…"
  },
  {
    id: "commune",
    label: "Comuna",
    field: "territory_commune.name.raw",
    placeholder: "Buscar comuna…"
  }
];

// Fields to search in when user types in search box
const searchFields = [
  "title",
  "requirements",
  "knowledges",
  "benefits"
];

// HitsWrapper component to handle Instantsearch hits
const PublicationsHitsWrapper = ({
  hits,
  setHits,
  is_public,
  canAdmin
}) => {
  const { hits: instantHits } = useHits();

  useEffect(() => {
    // Convert Instantsearch hits to the format expected by PublicationsHits
    const convertedHits = instantHits.map(hit => {
      // Prefer _source if it exists (it's the original structure from Elasticsearch)
      let source;
      if (hit._source && Object.keys(hit._source).length > 0) {
        source = hit._source;
      } else {
        // Reconstruct _source from spread data
        const { objectID, _id, _source, ...sourceData } = hit;
        source = sourceData;
      }

      const hitId = hit._id || hit.objectID || hit.id || source?.id;

      return {
        _id: hitId,
        _source: source
      };
    });

    setHits(convertedHits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instantHits]);

  if (hits.length === 0) {
    return (
      <div className="text-center p-4">
        <p className="text-muted">No se encontraron publicaciones</p>
      </div>
    );
  }

  return <PublicationsHits hits={hits} is_public={is_public} canAdmin={canAdmin} />;
};

const PublicationsIndex = (props) => {
  const { route, canAdmin = false, is_public = false, currentUserRole } = props;

  const [hits, setHits] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openFilterId, setOpenFilterId] = useState(null);

  // Store sort option in a ref to avoid recreating the client
  const [sortOption, setSortOption] = useState({ field: "created_at", order: "desc" });
  const sortOptionRef = useRef(sortOption);

  useEffect(() => {
    sortOptionRef.current = sortOption;
  }, [sortOption]);

  // Store filterConfigs and searchFields in refs
  const filterConfigsRef = useRef(filterConfigs);
  const searchFieldsRef = useRef(searchFields);

  // Listen for sort changes from CustomSortBy
  useEffect(() => {
    const handleSortChange = (event) => {
      const option = event.detail;
      setSortOption({ field: option.field, order: option.order });
    };

    window.addEventListener('search:sort', handleSortChange);
    return () => {
      window.removeEventListener('search:sort', handleSortChange);
    };
  }, []);

  // Create search client with Elasticsearch adapter
  const searchClient = useMemo(() => {
    const searchRoute = route.endsWith('/_search') ? route : `${route}/_search`;

    const adapter = createElasticsearchAdapter(searchRoute, {
      httpHeaders: { "X-CSRF-TOKEN": window.Hupe8.token },
      timeout: 20000
    });

    // Wrap the search method to inject defaultFilters, queryFields, and customSort dynamically
    const originalSearch = adapter.search.bind(adapter);
    adapter.search = async (requests) => {
      const filterAttributes = filterConfigsRef.current.map(config => config.field);

      const modifiedRequests = requests.map(req => ({
        ...req,
        params: {
          ...req.params,
          customSort: sortOptionRef.current,
          queryFields: searchFieldsRef.current,
          // Ensure facets are always requested for filter attributes
          facets: [...(req.params.facets || []), ...filterAttributes].filter((v, i, a) => a.indexOf(v) === i)
        }
      }));
      return originalSearch(modifiedRequests);
    };

    return adapter;
  }, [route]);

  return (
    <Card className="mb-3 material-shadow">
      <CardBody>
        <InstantSearch
          searchClient={searchClient}
          indexName="publications"
          future={{ preserveSharedStateOnUnmount: true }}
          routing={false}
        >
          <Row>
            <Col md="6">
              <CustomSearchBox
                queryFields={searchFields}
                placeholder="Buscar publicaciones..."
              />
              <CustomStats />
            </Col>
            <Col />
            <Col md="3" className="text-end">
              <Button
                color="secondary"
                outline
                size="sm"
                className="mb-2"
                style={{ minWidth: 110 }}
                onClick={() => setFiltersOpen(o => !o)}
              >
                <i className="fas fa-filter me-2" /> Filtros
              </Button>
              <CustomSortBy
                options={[
                  { label: "Más recientes", field: "created_at", order: "desc", defaultOption: true },
                  { label: "Más antiguas", field: "created_at", order: "asc" },
                  { label: "Título A-Z", field: "title.raw", order: "asc" },
                  { label: "Título Z-A", field: "title.raw", order: "desc" }
                ]}
                onSortChange={(option) => {
                  window.dispatchEvent(new CustomEvent('search:sort', { detail: option }));
                }}
              />
            </Col>
          </Row>

          <Collapse isOpen={filtersOpen} className="mb-2 filtros-compactos">
            <div className="bg-light border rounded p-3 shadow-sm">
              <Row>
                {filterConfigs.map(f => (
                  <Col xs={12} key={f.id} className="mb-2">
                    <Button
                      color="link"
                      className="p-0 mb-1 filtro-capitalizado"
                      style={{ fontWeight: "bold", fontSize: 13, textAlign: "left",
                        width: "100%", letterSpacing: "1px", textTransform: "uppercase" }}
                      onClick={() =>
                        setOpenFilterId(openFilterId === f.id ? null : f.id)
                      }
                    >
                      {f.label}
                      <span className="float-end">
                        {openFilterId === f.id ? "▲" : "▼"}
                      </span>
                    </Button>
                    <Collapse isOpen={openFilterId === f.id}>
                      <CustomRefinementList
                        attribute={f.field}
                        searchPlaceholder={f.placeholder}
                        searchable={false}
                      />
                    </Collapse>
                  </Col>
                ))}
              </Row>
              <Row className="mt-2">
                <Col className="d-flex justify-content-end">
                  <CustomClearRefinements translations={{ "reset.clear_all": "Limpiar filtros" }} />
                  <Button
                    color="light"
                    size="sm"
                    outline
                    className="ms-2"
                    onClick={() => setFiltersOpen(false)}
                  >
                    Cerrar
                  </Button>
                </Col>
              </Row>
            </div>
          </Collapse>

          <PublicationsHitsWrapper
            hits={hits}
            setHits={setHits}
            is_public={is_public}
            canAdmin={canAdmin}
          />

          {hits.length > 0 && (
            <CustomPagination />
          )}
        </InstantSearch>
      </CardBody>
    </Card>
  );
};

export default PublicationsIndex;
